import classNames from "classnames";
import { useRef } from "react";
import ReactDialKnob, {
  SkinWrap,
  KnobProps,
  useAngleUpdater,
  composeTwo,
} from "react-dial-knob";
import { valueToAllowedPanValues } from "./valueConverters";

export default function Knob(
  props: KnobProps & { theme: "default" | "white" }
): JSX.Element {
  const [angle, setAngle] = useAngleUpdater(props.value);
  const angleChangeHandler = composeTwo<number>(setAngle, props.onAngleChange);
  const centerTextSize = props.diameter * 0.35;
  const notchWidth = props.diameter * 0.075;
  const notchHeight = props.diameter * 0.2;
  const hasBeenInteractedWith = useRef(false);

  return (
    <SkinWrap>
      <ReactDialKnob
        diameter={props.diameter}
        value={props.value}
        min={props.min}
        max={props.max}
        step={props.step}
        spaceMaxFromZero={props.spaceMaxFromZero}
        ariaLabelledBy={props.ariaLabelledBy}
        ariaValueText={props.ariaValueText}
        knobStyle={{ cursor: "pointer", ...props.knobStyle }}
        onInteractionChange={() => {
          hasBeenInteractedWith.current = true;
        }}
        onValueChange={(value) => {
          // prevent the component from calling onValueChange before the user touches it
          if (!hasBeenInteractedWith.current) {
            return;
          }

          props.onValueChange?.(value);
        }}
        onAngleChange={angleChangeHandler}
      >
        <div className={classNames("group absolute inset-0")}>
          <div
            className={classNames(
              "absolute rounded-full bg-slate-500 transition-transform group-active:-translate-y-1"
            )}
            style={{
              width: props.diameter - 4,
              height: props.diameter - 4,
              left: 2,
              top: 3,
            }}
          />

          <div
            className={classNames(
              "relative rounded-full group-active:translate-y-0.5",
              {
                "bg-slate-300": props.theme === "default",
                "bg-white": props.theme === "white",
              }
            )}
            style={{
              width: props.diameter - 4,
              height: props.diameter - 4,
              left: 2,
              transform: `rotate(${angle}deg)`,
            }}
          >
            <div
              className="absolute left-1/2 top-0 -translate-x-1/2 rounded-b bg-black"
              style={{ width: notchWidth, height: notchHeight }}
            />
          </div>

          <div
            className={classNames(
              "absolute left-1/2 top-1/2 flex -translate-x-1/2 -translate-y-1/2 select-none items-center justify-center leading-none text-black"
            )}
            style={{ fontSize: centerTextSize }}
          >
            {valueToAllowedPanValues(props.value)}
          </div>
        </div>
      </ReactDialKnob>
    </SkinWrap>
  );
}
